import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/what-is-precast', label: 'What is Precast' },
  { to: '/construction', label: 'Construction Tech' },
  { to: '/brand-leasing', label: 'Brand Leasing' },
  { to: '/property-tech', label: 'IT & Property Tech' },
  { to: '/about', label: 'About' },
];

const Navigation = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-5 lg:px-12">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_infra-quick/artifacts/jcvhefb8_Picture1.png"
              alt="Arthaa Build"
              className="h-14 lg:h-20 w-auto"
            />
          </Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium tracking-wide transition-colors ${
                  isActive(link.to) ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`nav-${link.to === '/' ? 'home' : link.to.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] hover:from-[#1E3A5F] hover:to-[#2B5278] text-white font-semibold text-sm px-8 py-3 rounded-md transition-all shadow-md hover:shadow-lg"
              data-testid="nav-contact-cta"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 -mr-2 text-slate-700"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 top-20 z-40 transition-all duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />

        {/* Panel */}
        <div
          className={`relative bg-white w-full max-h-[calc(100vh-5rem)] overflow-y-auto shadow-xl transition-transform duration-300 ${
            open ? 'translate-y-0' : '-translate-y-4'
          }`}
        >
          <div className="px-6 py-6 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block py-3 px-4 rounded-lg text-base font-medium transition-colors ${
                  isActive(link.to)
                    ? 'bg-[#2B5278]/10 text-[#1E3A5F]'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`mobile-nav-${link.to === '/' ? 'home' : link.to.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3">
              <Link
                to="/contact"
                className="block text-center bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] text-white font-semibold py-3.5 rounded-lg shadow-md"
                data-testid="mobile-nav-contact-cta"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
